import {
  Anchor,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconDevices,
  IconGauge,
} from "@tabler/icons-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useSiteDevices, useSiteMeters, useSites } from "@/api/queries";
import type { DeviceSummary } from "@/api/types";
import { getDeviceKindMeta } from "@/components/deviceKinds";
import { ErrorBanner } from "@/components/ErrorBanner";
import { MeterReadingsTable } from "@/components/MeterReadingsTable";

export function SiteDetailPage() {
  const { siteSlug } = useParams<{ siteSlug: string }>();
  const [showMeterList, setShowMeterList] = useState(false);
  const sitesQuery = useSites({ search: siteSlug });
  const devicesQuery = useSiteDevices(siteSlug);
  const noDevices =
    devicesQuery.data !== undefined && devicesQuery.data.items.length === 0;
  const shouldShowMeterList = showMeterList || noDevices;
  const metersQuery = useSiteMeters(shouldShowMeterList ? siteSlug : undefined);

  const site = sitesQuery.data?.items.find((s) => s.slug === siteSlug);
  const meters = metersQuery.data?.items ?? [];
  const canFilterUngrouped = meters.some((meter) => "device_id" in meter);
  const visibleMeters = canFilterUngrouped
    ? meters.filter((meter) => meter.device_id === null)
    : meters;

  return (
    <Stack>
      <Breadcrumbs>
        <Anchor component={Link} to="/sites">
          Sites
        </Anchor>
        <Text>{site?.display_name ?? siteSlug ?? ""}</Text>
      </Breadcrumbs>

      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Title order={1}>{site?.display_name ?? siteSlug}</Title>
          {site && (
            <Text c="dimmed" size="sm">
              {site.slug} · {site.timezone}
            </Text>
          )}
        </Stack>
        {site && (
          <Badge color={site.status === "active" ? "green" : "gray"} variant="light">
            {site.status}
          </Badge>
        )}
      </Group>

      <Paper withBorder p="lg" radius="md">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <IconDevices size={20} />
            <Title order={3}>Devices</Title>
          </Group>
          {devicesQuery.data && (
            <Text c="dimmed" size="sm">
              {devicesQuery.data.page.total} device
              {devicesQuery.data.page.total === 1 ? "" : "s"}
            </Text>
          )}
        </Group>

        {devicesQuery.error && <ErrorBanner error={devicesQuery.error} />}

        {devicesQuery.isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : devicesQuery.data && devicesQuery.data.items.length > 0 ? (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {devicesQuery.data.items.map((device) => (
              <DeviceCard key={device.id} device={device} siteSlug={siteSlug} />
            ))}
          </SimpleGrid>
        ) : (
          <Text c="dimmed">No devices commissioned on this site yet.</Text>
        )}
      </Paper>

      <Paper withBorder p="lg" radius="md">
        <Group justify="space-between" mb={shouldShowMeterList ? "md" : 0}>
          <Group gap="xs">
            <IconGauge size={20} />
            <Title order={3}>
              {canFilterUngrouped ? "Ungrouped meters" : "Meters"}
            </Title>
          </Group>
          {!noDevices && (
            <Button
              variant="light"
              leftSection={
                showMeterList ? (
                  <IconChevronUp size={16} />
                ) : (
                  <IconChevronDown size={16} />
                )
              }
              onClick={() => setShowMeterList((current) => !current)}
            >
              {showMeterList ? "Hide meter list" : "View meter list"}
            </Button>
          )}
        </Group>

        {metersQuery.error && <ErrorBanner error={metersQuery.error} />}

        {shouldShowMeterList &&
          (metersQuery.isLoading ? (
            <Center py="xl">
              <Loader />
            </Center>
          ) : visibleMeters.length > 0 ? (
            <MeterReadingsTable meters={visibleMeters} siteSlug={siteSlug} />
          ) : (
            <Text c="dimmed">No ungrouped meters on this site.</Text>
          ))}
      </Paper>
    </Stack>
  );
}

type DeviceCardProps = {
  device: DeviceSummary;
  siteSlug: string | undefined;
};

function DeviceCard({ device, siteSlug }: DeviceCardProps) {
  const { color, Icon } = getDeviceKindMeta(device.kind);

  return (
    <Card
      component={Link}
      to={`/sites/${siteSlug ?? ""}/devices/${device.slug}`}
      withBorder
      padding="lg"
      radius="md"
    >
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon color={color} variant="light" size="lg">
              <Icon size={20} />
            </ThemeIcon>
            <Stack gap={0}>
              <Text fw={600} lineClamp={1}>
                {device.display_name}
              </Text>
              <Text c="dimmed" size="xs" lineClamp={1}>
                {device.slug}
              </Text>
            </Stack>
          </Group>
          <Badge
            variant="light"
            color={device.status === "active" ? "green" : "gray"}
          >
            {device.status}
          </Badge>
        </Group>
        <Group justify="space-between" wrap="nowrap">
          <Badge variant="light" color={color} tt="lowercase">
            {device.kind}
          </Badge>
          <Text c="dimmed" size="xs">
            {device.meter_count} meter{device.meter_count === 1 ? "" : "s"}
          </Text>
        </Group>
      </Stack>
    </Card>
  );
}
